import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import isEqual from "lodash/isEqual";
import { Flex, Text } from "styles/utility-components";
import z from "zod";

import { DetailsContent, DetailsHeader, DetailsPage } from "components/app_layout/DetailsLayout";
import FilterFormResults from "components/filter_form/filter_form_results/FilterFormResults";
import { EmptyNoticesV2 } from "components/notices_v2/EmptyNoticesV2";
import { ExportNoticesV2Button } from "components/notices_v2/ExportNoticesV2Button";
import { FilterForm } from "components/notices_v2/FilterForm";
import {
  DEFAULT_NOTICE_SEMANTIC_COLUMN_FIELDS,
  normalizeNoticeSemanticColumnFields,
  PICKER_NOTICE_SEMANTIC_COLUMNS,
} from "components/notices_v2/noticeSemanticColumns";
import {
  coerceNoticeSemanticSort,
  DEFAULT_NOTICE_SEMANTIC_SORT,
} from "components/notices_v2/noticeSemanticSort";
import { NoticeSemanticSortByCard } from "components/notices_v2/NoticeSemanticSortByCard";
import { DEFAULT_PAGINATION, NoticeTableV2 } from "components/notices_v2/NoticeTableV2";
import { TopicSearchSection } from "components/notices_v2/TopicSearchSection";
import { useNoticesV2UrlState } from "components/notices_v2/useNoticesV2UrlState";
import { useNoticesV2ViewStatus } from "components/notices_v2/useNoticesV2ViewStatus";
import {
  convertNoticeV2FiltersToSearchRequest,
  DEFAULT_FILTERS,
  getAppliedFilters,
  NoticeV2Filters,
  stageUnionToEnum,
} from "components/notices_v2/utils";
import SearchActions from "components/saved_searches/SearchActions";
import { useRecentViews } from "components/saved_searches/utils";
import FilterPanelToggle from "components/search_page/FilterPanelToggle";
import BetaIcon from "components/ui/beta_icon/BetaIcon";
import Sidebar from "components/ui/sidebar/SidebarOld";
import EditColumnsButton from "lib/core_components/EditColumnsButton";
import { RELEVANCY_SEARCH_NEW_USERS, useVariableValue } from "lib/featureFlags";
import { useCreditsStatus } from "lib/hooks/api/credits/useCreditsStatus";
import { useNoticeTopicSearch } from "lib/hooks/api/notices/useNoticeTopicSearch";
import { useSavedViewsDataType } from "lib/hooks/api/saved_views/useSavedViews";
import { useTeam } from "lib/hooks/api/teams/useTeam";
import { useLogSearch } from "lib/hooks/api/userActivity/useLogSearch";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useURLState } from "lib/hooks/useURLState";
import { createSavedViewSelectedEvent } from "lib/savedViewTracking";
import { EventDataTypes, EventNames, useTracking } from "lib/tracking";
import { LoggedEntityType, SavedViewDataType } from "lib/types/graphQLEnums";
import { TableSettings } from "lib/types/models";
import { NoticeSemanticColumnField, SavedNoticeSemanticView } from "lib/types/savedViews";

function convertInternalFiltersToSavedViewFilters(
  filters: NoticeV2Filters,
): SavedNoticeSemanticView["view"]["filterSettings"] {
  // Not spreading due to DEFAULT_FILTERS being based on notice v1
  return {
    topicSearch: filters.topicSearch,
    stage: filters.stage,
    buyerIds: filters.buyerIds,
    supplierIds: filters.supplierIds,
    frameworkIds: filters.frameworkIds,
    publishDate: filters.publishDate,
    expiryDate: filters.expiryDate,
    value: filters.value,
    buyerCategories: filters.buyerCategories,
    buyerListIds: filters.buyerListIds,
  };
}

export function NoticesV2Page() {
  const { logEvent } = useTracking();
  const isRelevancyNewUsers = useVariableValue(RELEVANCY_SEARCH_NEW_USERS, false);
  const { data: team } = useTeam();
  const user = useAssertCurrentUser();

  useCreditsStatus({ teamId: user.team.id });

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [seededTopicInput, setSeededTopicInput] = useState<string>();
  const {
    urlFilters,
    setFilters,
    setTopicSearch,
    debouncedSetFilters,
    urlColumnSettings,
    setColumnSettings,
    urlSemanticSort,
    setSemanticSort,
    pagination,
    setPaginationAndPersistPageSize,
    resetPaginationToFirstPage,
  } = useNoticesV2UrlState();
  const [activeGroups, setActiveGroups] = useState<string[]>([]);
  const [selectedNotices, setSelectedNotices] = useState<string[]>([]);
  const currentPage = pagination.page ?? 1;
  const pageSize = pagination.pageSize ?? DEFAULT_PAGINATION.pageSize;

  const { data: allNoticeSemanticSavedViewsQuery } = useSavedViewsDataType(
    SavedViewDataType.NoticeSemantic,
    {},
    { page: 1, pageSize: 1000 },
  );
  const [urlViewId, setUrlViewId] = useURLState(
    "current_view_id",
    undefined,
    z.string().optional(),
  );
  const selectedView = allNoticeSemanticSavedViewsQuery?.results.find((v) => v.id === urlViewId);

  const hasUrlFilterOverride = urlFilters !== undefined && !isEqual(urlFilters, DEFAULT_FILTERS);

  const filters = useMemo((): NoticeV2Filters => {
    if (urlFilters !== undefined && hasUrlFilterOverride) {
      return urlFilters;
    }
    // If we have a selected view, use its filters
    if (selectedView) {
      return {
        ...selectedView.view.filterSettings,
        stage: stageUnionToEnum(selectedView.view.filterSettings.stage),
      };
    }
    // Otherwise, use default filters
    return DEFAULT_FILTERS;
  }, [hasUrlFilterOverride, selectedView, urlFilters]);

  const columnFields = useMemo((): NoticeSemanticColumnField[] => {
    if (urlColumnSettings !== undefined) {
      return normalizeNoticeSemanticColumnFields(urlColumnSettings);
    }
    if (selectedView) {
      return normalizeNoticeSemanticColumnFields(
        selectedView.view.tableSettings.columns.map((c) => c.field),
      );
    }
    return DEFAULT_NOTICE_SEMANTIC_COLUMN_FIELDS;
  }, [urlColumnSettings, selectedView]);

  const sortableColumnFields = useMemo(
    () => PICKER_NOTICE_SEMANTIC_COLUMNS.map((column) => column.field),
    [],
  );

  const semanticSort = useMemo(
    () =>
      coerceNoticeSemanticSort(
        urlSemanticSort ?? selectedView?.view.sortOrder ?? DEFAULT_NOTICE_SEMANTIC_SORT,
        sortableColumnFields,
      ),
    [urlSemanticSort, selectedView, sortableColumnFields],
  );

  const { areSettingsEqualToDefault, haveSettingsChanged, viewStatus } = useNoticesV2ViewStatus({
    selectedView,
    filters,
    columnFields,
    semanticSort,
  });

  const topicSearch = filters.topicSearch?.trim() ?? "";
  const hasSearchQuery = topicSearch.length > 0;
  const filtersInput = useMemo(
    (): NoticeV2Filters => ({
      ...filters,
      topicSearch,
    }),
    [filters, topicSearch],
  );

  const searchRequest = useMemo(
    () =>
      convertNoticeV2FiltersToSearchRequest(
        filtersInput,
        {
          current: currentPage,
          pageSize,
        },
        semanticSort,
      ),
    [filtersInput, currentPage, pageSize, semanticSort],
  );

  const { data, isLoading, isError } = useNoticeTopicSearch(searchRequest, {
    enabled: hasSearchQuery,
  });
  useLogSearch(LoggedEntityType.Notice, hasSearchQuery ? searchRequest : undefined);

  const results = hasSearchQuery ? data?.results ?? [] : [];
  const totalResults = hasSearchQuery ? data?.totalResults ?? 0 : 0;

  const isSearchSettled = hasSearchQuery && !isLoading && !isError;
  const shouldShowEmptyStatePanel = !hasSearchQuery || (isSearchSettled && totalResults === 0);

  const handlePageChange = (page: number, newPageSize: number) => {
    setPaginationAndPersistPageSize(page, newPageSize);
  };

  const setSelectedView = (viewId?: string, viewName?: string, context?: string) => {
    if (!viewId) {
      setUrlViewId(undefined);
      setFilters(undefined);
      setColumnSettings(undefined);
      setSemanticSort(undefined);
      resetPaginationToFirstPage();
      return;
    }

    setUrlViewId(viewId);
    setFilters(undefined);
    setColumnSettings(undefined);
    setSemanticSort(undefined);
    resetPaginationToFirstPage();
    logEvent(
      EventNames.viewSelected,
      createSavedViewSelectedEvent("noticeSemantic", viewId, viewName, context),
    );
  };

  const { recentViewsIds, setRecentViewsIds } = useRecentViews(
    SavedViewDataType.NoticeSemantic,
    false,
  );

  useEffect(() => {
    if (urlViewId && recentViewsIds[0] !== urlViewId) {
      // Add view to recent views or moves it to the top if already exists
      setRecentViewsIds([urlViewId, ...recentViewsIds.filter((v) => v !== urlViewId)]);
    }
  }, [urlViewId, setRecentViewsIds, recentViewsIds]);

  useEffect(() => {
    if (!isRelevancyNewUsers) return;
    const topic = team?.metadata?.defaultSearchTopic?.trim();
    const shouldSkipSeedInput =
      !topic || urlViewId || selectedView || hasUrlFilterOverride || seededTopicInput !== undefined;
    if (shouldSkipSeedInput) return;
    setSeededTopicInput(topic);
  }, [
    isRelevancyNewUsers,
    team?.metadata?.defaultSearchTopic,
    urlViewId,
    selectedView,
    hasUrlFilterOverride,
    seededTopicInput,
  ]);

  const viewTitle =
    selectedView?.name ?? (isRelevancyNewUsers ? "Find notices" : "Find notices 2.0");

  const headerMeasureRef = useRef<HTMLDivElement>(null);
  const [stickyHeaderOffsetPx, setStickyHeaderOffsetPx] = useState(0);

  useLayoutEffect(() => {
    const el = headerMeasureRef.current;
    if (!el) return;

    const update = () => {
      setStickyHeaderOffsetPx(Math.round(el.getBoundingClientRect().height));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <DetailsPage>
      <div ref={headerMeasureRef}>
        <DetailsHeader>
          <Flex gap={8} justifyContent="space-between" alignItems="center">
            <Flex gap={8} alignItems="center">
              <Flex gap={8} alignItems="center">
                <Text h1>{viewTitle}</Text>
                {viewStatus && (
                  <ViewStatus description data-status={viewStatus.status}>
                    {viewStatus.message}
                  </ViewStatus>
                )}
              </Flex>
              {!isRelevancyNewUsers && <BetaIcon betaFeature="hybridSearch" />}
            </Flex>
            <SearchActions
              dataType="noticeSemantic"
              selectedView={selectedView}
              setSelectedView={setSelectedView}
              currentView={{
                areSettingsEqualToDefault,
                haveSettingsChanged,
                view: {
                  sortOrder: semanticSort,
                  tableSettings: {
                    columns: columnFields.map((field) => ({ field })),
                  },
                  filterSettings: convertInternalFiltersToSavedViewFilters(filters),
                } satisfies SavedNoticeSemanticView["view"],
              }}
              showGetAlertsShortcut
            />
          </Flex>
        </DetailsHeader>
      </div>
      <PageContent>
        <PageMainRow>
          <Sidebar open={isSidebarOpen} offsetTop={stickyHeaderOffsetPx}>
            <Flex column>
              <TopicSearchSection
                topicInput={
                  filters.topicSearch?.trim() ? filters.topicSearch : seededTopicInput ?? ""
                }
                onSearch={(value) => {
                  resetPaginationToFirstPage();
                  setSeededTopicInput(undefined);
                  setTopicSearch(value, filters);
                }}
                disabled={hasSearchQuery && isLoading}
                onCollapseSidebar={() => setIsSidebarOpen(false)}
                showCollapseButton={hasSearchQuery}
                defaultSearchTopic={team?.metadata?.defaultSearchTopic ?? ""}
                showDefaultSearchTopicHint={
                  isRelevancyNewUsers && !!team?.metadata?.defaultSearchTopic?.trim()
                }
                onDefaultSearchTopicUpdated={(newTopic) => {
                  const previousDefault = team?.metadata?.defaultSearchTopic?.trim() ?? "";
                  const currentInput =
                    filters.topicSearch?.trim() || seededTopicInput?.trim() || "";
                  if (currentInput !== previousDefault) {
                    return;
                  }
                  resetPaginationToFirstPage();
                  setSeededTopicInput(undefined);
                  setTopicSearch(newTopic, filters);
                }}
              />
              <FilterForm
                onChange={(newFilters) => {
                  debouncedSetFilters(newFilters);
                  resetPaginationToFirstPage();
                }}
                filters={filters}
                onClose={() => setIsSidebarOpen(false)}
                activeGroups={activeGroups}
                onActiveGroupsChange={setActiveGroups}
                isSidebar
                filtersDisabled={!hasSearchQuery}
                onSideBarOpenChange={setIsSidebarOpen}
                hideSidebarCollapseButton
                key={selectedView?.id}
              />
            </Flex>
          </Sidebar>
          <TableMainArea>
            {shouldShowEmptyStatePanel ? (
              <EmptyNoticesV2
                variant={hasSearchQuery ? "noResults" : "beforeSearch"}
                onSelectPinnedSearch={(viewId, viewName) =>
                  setSelectedView(viewId, viewName, "Notices V2 empty state pinned")
                }
              />
            ) : (
              <Flex gap={8} justifyContent="space-between" column>
                <Flex gap={8} justifyContent="space-between" alignItems="flex-start">
                  <Flex alignItems="center" gap={8}>
                    <FilterPanelToggle
                      open={isSidebarOpen}
                      onOpenChange={setIsSidebarOpen}
                      appliedFiltersCount={getAppliedFilters(filters).length}
                    />
                    <FilterFormResults
                      totalResults={totalResults}
                      isLoading={hasSearchQuery && isLoading}
                    />
                  </Flex>
                  <Flex alignItems="flex-start" gap={8}>
                    {selectedNotices.length === 0 && (
                      <EditColumnsButton<TableSettings<NoticeSemanticColumnField>>
                        selectedColumns={columnFields.map((field) => ({ field }))}
                        allAvailableColumns={PICKER_NOTICE_SEMANTIC_COLUMNS}
                        onNewColumnSettingChange={(columns) => {
                          setColumnSettings(
                            normalizeNoticeSemanticColumnFields(columns.map((c) => c.field)),
                          );
                          resetPaginationToFirstPage();
                        }}
                        dataType={EventDataTypes.noticeSemantics}
                        currentViewId={selectedView?.id}
                        trackLegacyReorderEvent={false}
                      />
                    )}
                    <NoticeSemanticSortByCard
                      sort={semanticSort}
                      columnFields={sortableColumnFields}
                      disabled={!hasSearchQuery}
                      onSortChange={(next) => {
                        const isDefaultSort = isEqual(next, DEFAULT_NOTICE_SEMANTIC_SORT);
                        const savedViewWouldResurfaceSort =
                          selectedView !== undefined &&
                          !isEqual(selectedView.view.sortOrder, DEFAULT_NOTICE_SEMANTIC_SORT);
                        // If we cleared the URL here, `urlSemanticSort ?? selectedView.sortOrder` would
                        // bring the saved non-default sort back; keep an explicit default in the URL.
                        if (isDefaultSort && savedViewWouldResurfaceSort) {
                          setSemanticSort(DEFAULT_NOTICE_SEMANTIC_SORT);
                        } else {
                          setSemanticSort(isDefaultSort ? undefined : next);
                        }
                        resetPaginationToFirstPage();
                      }}
                    />
                    <ExportNoticesV2Button
                      filters={filtersInput}
                      semanticSort={semanticSort}
                      selectedNotices={selectedNotices}
                      enabled={
                        hasSearchQuery && // TODO: Remove selectedNotices check once backend supports subset
                        selectedNotices.length === 0
                      }
                      totalResults={totalResults}
                      isLoadingTotalResults={hasSearchQuery && isLoading}
                    />
                  </Flex>
                </Flex>
                <NoticeTableV2
                  filters={filters}
                  columnSettings={columnFields}
                  selectedRows={selectedNotices}
                  onSelectedRowsChange={(rows) => setSelectedNotices(rows)}
                  results={results}
                  totalResults={totalResults}
                  isLoading={hasSearchQuery && isLoading}
                  isError={isError}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  onPageChange={handlePageChange}
                  emptyText={
                    <EmptyNoticesV2
                      onSelectPinnedSearch={(viewId, viewName) =>
                        setSelectedView(viewId, viewName, "Notices V2 table empty pinned")
                      }
                    />
                  }
                />
              </Flex>
            )}
          </TableMainArea>
        </PageMainRow>
      </PageContent>
    </DetailsPage>
  );
}

const PageContent = styled(DetailsContent)({
  padding: 0,
  display: "flex",
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
});

const PageMainRow = styled(Flex)({
  width: "100%",
  height: "100%",
  flex: 1,
  minHeight: 0,
});

const TableMainArea = styled.div({
  flex: "1 1 0",
  minWidth: 0,
  // Allow this flex item to shrink below content height so overflow can scroll (flex default min-height: auto prevents that).
  minHeight: 0,
  overflowY: "auto",
  padding: "16px 24px",
});

const ViewStatus = styled(Text)(({ theme }) => ({
  padding: "2px 8px",
  '&[data-status="saved"]': {
    background: theme.colors.sysPrimarySubtle,
    color: theme.colors.sysTextDefault,
  },
  '&[data-status="unsaved"]': {
    background: theme.colors.sysWarningSubtle,
    color: "#7C4F07",
  },
}));
